import React, { useId } from 'react';

import { Container, Label, Hint, Control, Affixed, Affix } from './styles';

interface FieldProps {
  label: string;
  /** Texto de apoio abaixo do rótulo. */
  hint?: string;
  /** Mensagem de erro — aparece junto do campo, não num alerta global. */
  error?: string;
  required?: boolean;
  /** Ocupa a linha inteira do grid. */
  full?: boolean;
  /** Texto fixo antes do valor (ex.: "R$"). */
  prefix?: string;
  /** Texto fixo depois do valor (ex.: "%"). */
  suffix?: string;
  children: React.ReactNode;
}

const NATIVE_CONTROLS = ['input', 'textarea', 'select'];

/** `<input>`/`<textarea>` nativo ou um styled-component deles (TextInput, TextArea). */
const isNativeControl = (node: React.ReactNode): node is React.ReactElement => {
  if (!React.isValidElement(node)) return false;
  const type = node.type as unknown as string | { target?: unknown };
  if (typeof type === 'string') return NATIVE_CONTROLS.includes(type);
  return typeof type?.target === 'string' && NATIVE_CONTROLS.includes(type.target);
};

/**
 * Campo de formulário do admin: rótulo associado ao controle, dica e erro no
 * mesmo bloco. Quando o filho é um input/textarea, ele recebe o `id` do
 * rótulo, `aria-describedby` da dica/erro e `aria-invalid` — o leitor de tela
 * anuncia "Preço, R$, campo de edição" em vez de um campo sem nome. Filhos
 * compostos (ex.: OptionsEditor) ficam como estão.
 */
const Field: React.FC<FieldProps> = ({
  label,
  hint,
  error,
  required = false,
  full = false,
  prefix,
  suffix,
  children,
}) => {
  const id = useId();
  const helpId = hint || error ? `${id}-help` : undefined;

  const control = isNativeControl(children)
    ? React.cloneElement(children, {
        id: children.props.id ?? id,
        'aria-describedby': helpId,
        'aria-invalid': error ? true : undefined,
      })
    : children;

  return (
    <Container full={full}>
      <Label htmlFor={id}>
        {label}
        {required && <span aria-hidden> *</span>}
      </Label>

      <Control invalid={!!error}>
        {prefix || suffix ? (
          <Affixed invalid={!!error}>
            {prefix && <Affix aria-hidden>{prefix}</Affix>}
            {control}
            {suffix && <Affix aria-hidden>{suffix}</Affix>}
          </Affixed>
        ) : (
          control
        )}
      </Control>

      {(hint || error) && (
        <Hint id={helpId} invalid={!!error} role={error ? 'alert' : undefined}>
          {error || hint}
        </Hint>
      )}
    </Container>
  );
};

export default Field;
